import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Card, Icon, LinearProgress, Row, Stack, Text } from '@/ui';
/**
 * CartSummary Component - Steve Jobs Redesign
 *
 * Total is king. Details are secondary.
 * - Prominent total display
 * - Details hidden in collapsible section
 * - Free shipping badge when applicable
 *
 * @file CartSummary.tsx
 * @version 4.0.0 - Steve Jobs redesign
 */
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ChevronDown, Gift } from 'lucide-react';
import { useCartTotal } from '../hooks/useCart';
import { useCheckoutStore } from '@/features/checkout';
import { formatPrice } from '@/shared/utils';
import { CartSummarySkeleton } from '@/ui';

const FREE_SHIPPING_THRESHOLD = 10000; // $100 USD in minor units (cents)

const totalScaleAnimation = {
  initial: { scale: 1.05 },
  animate: { scale: 1 },
} as const;

export const CartSummary = React.memo(function CartSummary() {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const shippingMethod = useCheckoutStore((state) => state.shippingMethod);
  const { data: cartTotal, isLoading, isError } = useCartTotal(shippingMethod?.id);


  if (isLoading) {
    return <CartSummarySkeleton />;
  }

  if (isError || !cartTotal) {
    return (
      <Stack gap={2} align="center" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
        <Text size="sm" variant="error">
          {t('ui.cart.failedToLoadSummary')}
        </Text>
        <Text size="xs" variant="muted">
          {t('ui.cart.pleaseTryAgain')}
        </Text>
      </Stack>
    );
  }

  const currency = cartTotal.currency;
  const isFreeShipping = cartTotal.shipping_cost_minor_units === 0;
  const amountToFreeShipping = FREE_SHIPPING_THRESHOLD - cartTotal.subtotal_minor_units;
  const showFreeShippingProgress = !isFreeShipping && amountToFreeShipping > 0;

  return (
    <Stack gap={4} style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
      {/* Free Shipping Badge - Prominent when unlocked */}
      {isFreeShipping && cartTotal.subtotal_minor_units > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card variant="subtle" padding="sm" style={{ backgroundColor: 'hsl(var(--status-success) / 0.1)' }}>
            <Row align="center" gap={2}>
              <Icon icon={Gift} size="sm" color="success" />
              <Text size="sm" weight="medium" variant="success">
                {t('ui.cart.unlockedFreeShipping')}
              </Text>
            </Row>
          </Card>
        </motion.div>
      )}

      {/* Free Shipping Progress - Subtle */}
      {showFreeShippingProgress && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <Card variant="subtle" padding="sm">
            <Stack gap={1}>
              <Text size="xs" variant="muted">
                <Trans
                  i18nKey="ui.cart.addMoreForFreeShipping"
                  values={{ amount: formatPrice(amountToFreeShipping, currency) }}
                  components={{ strong: <strong /> }}
                />
              </Text>
              <LinearProgress
                value={Math.min((cartTotal.subtotal_minor_units / FREE_SHIPPING_THRESHOLD) * 100, 100)}
                size="xs"
                variant="default"
                animated={true}
              />
            </Stack>
          </Card>
        </motion.div>
      )}

      {/* Total - THE HERO */}
      <Stack gap={2}>
        <Row justify="between" align="baseline">
          <Text size="lg" weight="semibold" variant="default">
            {t('ui.cart.total')}
          </Text>
          <motion.div
            key={cartTotal.total_amount_minor_units}
            initial={totalScaleAnimation.initial}
            animate={totalScaleAnimation.animate}
          >
            <Text size="heading-2" weight="bold" variant="default">
              {formatPrice(cartTotal.total_amount_minor_units, currency)}
            </Text>
          </motion.div>
        </Row>

        {/* Details - Collapsible */}
        <Accordion type="single" collapsible value={showDetails ? 'details' : ''} onValueChange={(value) => setShowDetails(value === 'details')}>
          <AccordionItem value="details" style={{ border: 'none' }}>
            <AccordionTrigger style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
              <Row align="center" gap={1}>
                <Text size="xs" variant="muted">
                  {t('ui.cart.viewDetails')}
                </Text>
                <Icon icon={ChevronDown} size="xs" />
              </Row>
            </AccordionTrigger>
            <AccordionContent>
              <Stack gap={2} style={{ paddingTop: '0.5rem' }}>
                <Row justify="between">
                  <Text size="xs" variant="muted">
                    {t('ui.cart.subtotalWithCount', { count: cartTotal.item_count })}
                  </Text>
                  <Text size="xs" variant="muted">
                    {formatPrice(cartTotal.subtotal_minor_units, currency)}
                  </Text>
                </Row>

                <Row justify="between">
                  <Text size="xs" variant="muted">
                    {t('ui.cart.vatIncluded', { percent: (cartTotal.vat_percentage * 100).toFixed(0) })}
                  </Text>
                  <Text size="xs" variant="muted">
                    {formatPrice(cartTotal.vat_amount_minor_units, currency)}
                  </Text>
                </Row>

                <Row justify="between">
                  <Text size="xs" variant="muted">
                    {t('ui.cart.shipping')}
                  </Text>
                  {isFreeShipping ? (
                    <Text size="xs" weight="medium" variant="success">
                      {t('ui.cart.free')}
                    </Text>
                  ) : (
                    <Text size="xs" variant="muted">
                      {formatPrice(cartTotal.shipping_cost_minor_units, currency)}
                    </Text>
                  )}
                </Row>
              </Stack>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Stack>
    </Stack>
  );
});
